/**
 * ===========================================================================
 *  GRUNT AI — the ring, and the attack-token pool that makes it read
 * ===========================================================================
 *
 * The single most recognisable behaviour in Dynasty Warriors is what a mob of
 * peons does when you stand in it: they close to about 2–4 m, form a loose
 * ring, and then **only two or three of them ever swing at once**. The rest
 * circle, shuffle in and out, raise a guard, shout, and wait their turn. Every
 * frame in which twelve men attack simultaneously reads as a bad demo, no
 * matter how good the animation is.
 *
 * So the ring is not emergent here, it is explicit:
 *
 *   - every attackable target owns an **attack-token pool** (`store.tokN`),
 *     sized by difficulty (2 for the player on easy, 3 on normal/hard, 2 for
 *     unit-vs-unit fights);
 *   - a grunt may only enter WINDUP if it can claim a token, and it holds that
 *     token for the whole windup → swing → recovery;
 *   - grants for one target are additionally spaced by `grantGap` seconds, so
 *     two swings never *start* on the same frame even when two tokens are free.
 *
 * Everything else — the orbit, the in/out breathing, the taunts, the feint
 * steps, the blocks — exists to make the ~85 % of the ring that is *not*
 * attacking look busy instead of parked.
 */
import { ST, TEAM, KIND, FLAG } from '../core/entities.js';

import { ACT, LOCKED, ROLE, AIF, ORDERS, SECTORS, SECT_A, sectAngle, sectOf, frac01, hash32 } from './store.js';
import { seek, flee, separate, integrate, ballistic, deathSlide } from './steering.js';
import { _slot } from './squad.js';
import { WALLS } from '../world/walls.js';

/**
 * `Math.hypot` is NOT a cheap intrinsic in V8: it is a variadic builtin that
 * boxes its arguments, and it measured at ~155 bytes of garbage per call
 * against ~0 for `Math.sqrt`. This module calls it once or more per unit per
 * fixed step, which is the whole of the AI's allocation rate and therefore the
 * whole of `aiCpuPeak` — the mean was never the problem, the scavenges landing
 * inside the unit loop were. Same arithmetic, no allocation.
 */
const hyp = (x, z) => Math.sqrt(x * x + z * z);


const ATTACKS = ['attack_light_1', 'attack_light_2', 'attack_light_3', 'attack_light_4'];
/** Shared options object — anim.play() reads it synchronously and keeps nothing. */
const _ao = { blend: 0.1 };
/** One reused beam/sound bag for the arrow. Never retained by the callee. */
const _arrow = { life: 0.10, width: 0.05, width2: 0.018, style: 0, bright: 1.7, color: 0xfff0c8, alpha: 0.95 };
const _bow = { x: 0, y: 0, z: 0, gain: 0.85 };
/**
 * Global spacing between archer releases. It used to be kept in
 * `store.grantT[tgt]` — the same slot the MELEE token pool uses to space its
 * grants — so every arrow loosed at the hero also pushed the melee grant clock
 * forward and quietly starved the ring of swings. Archers get their own clock.
 */
let arcT = -9;

/** Restore the battle-wide archer cadence clock on mission retry. */
export function resetGruntBattle() { arcT = -9; }

/** Ranges, metres. */
const R_ATTACK = 2.35;     // how close a grunt must be to commit to a swing
const R_HIT = 2.9;         // how far the blade actually reaches
const R_RING = 4.6;        // outside this, run in
const R_SENSE = 16;        // notice range in the vision cone
const R_NEAR = 6.5;        // notice range regardless of facing
const R_KEEP = 30;         // beyond this the target is forgotten
const R_ARCHER = 9.0;      // archers hold this distance
/**
 * How far past his own formation a man will chase a foe. Without this every
 * unit independently seeks its own nearest enemy, two squads meeting each
 * other dissolve into interleaved red and blue, and a wide shot has no front
 * line anywhere on it. The hero is exempt — a Warriors field DOES converge on
 * him, and that is the one place the mob is supposed to lose its shape.
 */
const LEASH = 24;
/**
 * Second rank stands this much further out than the first. It has to clear the
 * commit range (3.2-3.4 m) by a good margin or the "second rank" is just more
 * men inside the same crush: at +1.6 m the ring measured 29 bodies inside 4 m,
 * which is denser than any Warriors game and reads as a scrum.
 */
const RANK_GAP = 2.42;

/* ============================================================== targeting */

function pickTarget(ctx, id) {
  const w = ctx.w, e = w.entities, A = ctx.store;
  const x = e.x[id], z = e.z[id];
  const myTeam = e.team[id];
  const foeTeam = myTeam === TEAM.PLAYER ? TEAM.ENEMY : TEAM.PLAYER;

  let tgt = A.tgt[id];
  if (tgt >= 0) {
    if (!e.alive[tgt] || e.uid[tgt] !== A.tgtUid[id] || e.state[tgt] === ST.DEAD) tgt = -1;
    else {
      const dx = e.x[tgt] - x, dz = e.z[tgt] - z;
      if (dx * dx + dz * dz > R_KEEP * R_KEEP) tgt = -1;
    }
  }

  // The player is a magnet — a Dynasty Warriors field converges on him. How
  // strongly is a personality trait, so the pull looks like eagerness rather
  // than like a global rule.
  const pid = w.playerId;
  if (myTeam === TEAM.ENEMY && pid >= 0 && e.alive[pid]) {
    const dx = e.x[pid] - x, dz = e.z[pid] - z;
    const d2 = dx * dx + dz * dz;
    const pull = 8 + A.agg[id] * 9;
    if (d2 < pull * pull) {
      if (tgt < 0) tgt = pid;
      else if (tgt !== pid) {
        const ex = e.x[tgt] - x, ez = e.z[tgt] - z;
        if (d2 < (ex * ex + ez * ez) * 1.6) tgt = pid;
      }
    }
  }
  const sq = A.squad[id];
  const S = ctx.squads;
  const engaged = sq >= 0 && S.alive[sq] && S.order[sq] === ORDERS.ENGAGE;
  // A man whose squad is in contact looks further than a man on the march: the
  // squad order already says there is a fight, and a rear rank that can only
  // see 16 m stands around inside its own battle.
  if (tgt < 0) tgt = e.nearest(x, z, engaged ? 28 : A.alert[id] ? R_SENSE + 6 : R_SENSE, foeTeam, id);
  // Nobody fights through masonry. Without this the whole converging field
  // piles onto the near face of a wall with the player on the far side.
  if (tgt >= 0 && WALLS.n && WALLS.blocked(x, z, e.x[tgt], e.z[tgt], 0)) tgt = -1;

  // ---- the leash ------------------------------------------------------
  if (tgt >= 0 && tgt !== pid && sq >= 0 && S.alive[sq] && S.order[sq] !== ORDERS.ROUT) {
    const bx = e.x[tgt] - S.ax[sq], bz = e.z[tgt] - S.az[sq];
    const L = engaged ? LEASH + 8 : LEASH;
    if (bx * bx + bz * bz > L * L) tgt = -1;
  }

  const prev = A.tgt[id];
  if (tgt >= 0) { A.tgt[id] = tgt; A.tgtUid[id] = e.uid[tgt]; }
  else { A.tgt[id] = -1; }
  if (tgt !== prev) { A.sect[id] = -1; A.rank[id] = 0; }   // his ring slot belonged to the old man
  return tgt;
}

/* ============================================================ ring slots */

/**
 * Claim an angular slot on `tgt`'s ring.
 *
 * The old code seeded the man's standing bearing from the bearing he already
 * had, so a wave arriving from one side simply stayed on that side and the
 * "ring" was a crescent with a 6 m hole in the back of it. Here every target
 * owns 16 inner slots and 16 outer ones, rebuilt from the live claims every
 * census, and a man may not stand where a man already stands: the crowd walks
 * itself around the hole. Overflow goes to the SECOND RANK rather than pushing
 * the whole ring outward — pushing the ring outward is what made a big mob
 * safer than a small one, because everybody ended up beyond his own commit
 * range.
 *
 * O(1) amortised: at most 16 byte reads.
 */
function claimSlot(ctx, id, tgt, wantA) {
  const A = ctx.store, R = A.ringSlot;
  const base = tgt << 5;
  const s0 = sectOf(wantA);
  const cur = A.sect[id], curRank = A.rank[id];
  // already standing in a slot of my own, and nobody is contesting it
  if (cur === s0 && R[base + (curRank << 4) + cur] <= 1) return;
  if (cur >= 0) { const k = base + (curRank << 4) + cur; if (R[k] > 0) R[k]--; }

  for (let k = 0; k <= SECTORS >> 1; k++) {
    for (let g = 0; g < 2; g++) {
      const s = (s0 + (g ? -k : k) + SECTORS) & (SECTORS - 1);
      if (R[base + s] === 0) { R[base + s] = 1; A.sect[id] = s; A.rank[id] = 0; snap(A, id, s); return; }
      if (k === 0) break;
    }
  }
  for (let k = 0; k <= SECTORS >> 1; k++) {
    for (let g = 0; g < 2; g++) {
      const s = (s0 + (g ? -k : k) + SECTORS) & (SECTORS - 1);
      if (R[base + SECTORS + s] === 0) { R[base + SECTORS + s] = 1; A.sect[id] = s; A.rank[id] = 1; snap(A, id, s); return; }
      if (k === 0) break;
    }
  }
  // thirty-two men on one target: stand behind, wherever you came from
  R[base + SECTORS + s0]++; A.sect[id] = s0; A.rank[id] = 1; snap(A, id, s0);
}

/** Point the man at the centre of the slot he just took, with a little slop. */
function snap(A, id, s) {
  A.ringA[id] = sectAngle(s) + (frac01(A.spawnKey[id], s + 3) - 0.5) * SECT_A * 0.5;
}

/** Standing radius for the rank this man holds. */
function standR(A, id) { return A.ringR[id] + (A.rank[id] ? RANK_GAP : 0); }

/** Cone + distance awareness check. Returns true when the unit wakes up. */
function notices(ctx, id, tgt) {
  const w = ctx.w, e = w.entities, A = ctx.store;
  if (A.alert[id]) return true;
  const dx = e.x[tgt] - e.x[id], dz = e.z[tgt] - e.z[id];
  const d2 = dx * dx + dz * dz;
  if (d2 < R_NEAR * R_NEAR) return true;
  if (d2 > R_SENSE * R_SENSE) return false;
  const d = Math.sqrt(d2);
  const fx = Math.sin(e.yaw[id]), fz = Math.cos(e.yaw[id]);
  return (fx * dx + fz * dz) / d > 0.34;               // ±70°
}

/* ================================================================= tokens */

function takeToken(ctx, id, tgt) {
  const A = ctx.store, w = ctx.w;
  // A hero on the floor, in a guard break or still in his hit reaction is the
  // one moment a Warriors mob stops taking turns and piles on. `ctx.open` is
  // raised by ai.js while the player is in one of those states.
  const player = tgt === w.playerId;
  const budget = player ? ctx.tokens + (ctx.open ? 2 : 0) : ctx.tokensAI;
  if (A.tokN[tgt] >= budget) return false;
  const gap = player && ctx.open ? ctx.grantGap * 0.45 : ctx.grantGap;
  if (w.time.sim - A.grantT[tgt] < gap) return false;
  A.tok[id] = 1; A.tokN[tgt]++; A.grantT[tgt] = w.time.sim;
  return true;
}
export function dropToken(A, id) {
  if (!A.tok[id]) return;
  A.tok[id] = 0;
  const t = A.tgt[id];
  if (t >= 0 && A.tokN[t] > 0) A.tokN[t]--;
}

/* ================================================================ actions */

function startSwing(ctx, id, tgt) {
  const w = ctx.w, e = w.entities, A = ctx.store;
  const ranged = A.role[id] === ROLE.ARCHER;
  const ci = (A.clip[id] + 1 + (hash32(A.spawnKey[id] + w.time.tick) & 1)) & 3;
  A.clip[id] = ci;
  const name = ranged ? ATTACKS[1] : ATTACKS[ci];
  const dur = (w.anim?.duration?.(name) || 0.6);
  A.act[id] = ACT.WINDUP;
  A.actT[id] = 0;
  A.dur[id] = dur * 0.86;                       // finish inside the anim lock
  e.state[id] = ST.WINDUP; e.stateT[id] = 0;
  _ao.blend = 0.08; w.anim?.play?.(id, name, _ao);
}

/** Deal the swing's damage if the target is still in the arc. */
function land(ctx, id, tgt) {
  const w = ctx.w, e = w.entities, A = ctx.store;
  if (tgt < 0 || !e.alive[tgt] || e.state[tgt] === ST.DEAD) return;
  const dx = e.x[tgt] - e.x[id], dz = e.z[tgt] - e.z[id];
  const d = hyp(dx, dz);
  const ranged = A.role[id] === ROLE.ARCHER;
  const reach = ranged ? 18 : R_HIT + (e.radius[tgt] || 0.42);
  if (d > reach) return;
  if (!ranged) {
    const fx = Math.sin(e.yaw[id]), fz = Math.cos(e.yaw[id]);
    if ((fx * dx + fz * dz) / (d || 1) < 0.45) return;    // swung past him
  } else {
    // An arrow that is neither seen nor heard is damage from nowhere: the hero
    // was losing health to bowmen up to 18 m away with nothing on screen.
    const pid = w.playerId;
    const px = pid >= 0 ? e.x[pid] : e.x[id], pz = pid >= 0 ? e.z[pid] : e.z[id];
    const vx = e.x[id] - px, vz = e.z[id] - pz;
    if (vx * vx + vz * vz < 46 * 46) {
      w.vfx?.beam?.(e.x[id], e.y[id] + 1.35, e.z[id], e.x[tgt], e.y[tgt] + 1.05, e.z[tgt], _arrow);
      _bow.x = e.x[id]; _bow.y = e.y[id] + 1.4; _bow.z = e.z[id];
      w.audio?.play?.('bow', _bow);
    }
  }
  const amt = (ranged ? 5 : 11) * ctx.damageMul * (0.8 + frac01(A.spawnKey[id], w.time.tick & 255) * 0.5);
  ctx.hurt(tgt, amt, id, dx / (d || 1), dz / (d || 1), 0);
}

/* ============================================================== the brain */

/**
 * One decision. Called at most every ~0.2–0.4 s per unit, staggered by its
 * deterministic battle-local spawn key,
 * which is what keeps 600 units under a millisecond of thinking.
 */
export function think(ctx, id) {
  const w = ctx.w, e = w.entities, A = ctx.store;
  const u = A.spawnKey[id];
  const salt = w.time.tick & 4095;
  A.think[id] = 0.18 + frac01(u, salt) * 0.22;
  const act = A.act[id];
  if (LOCKED[act]) return;

  const sq = A.squad[id];
  const S = ctx.squads;
  const flags = A.flags[id];
  const vet = (flags & AIF.VETERAN) !== 0;

  // ---- morale ---------------------------------------------------------
  // The old test was a single hard-coded `m < 0.16`, which with a 0.25 floor
  // from team morale needed a man to watch a dozen comrades die inside 8.5 m
  // and never fired once in a whole capture. Courage is a personality trait,
  // veterans hold, and a squad that has been gutted takes its men with it.
  const teamMorale = ctx.morale[e.team[id]] ?? 0.5;
  const m = e.morale[id] * 0.75 + teamMorale * 0.25;
  const squadRout = sq >= 0 && S.alive[sq] && S.order[sq] === ORDERS.ROUT;
  let brk = 0.32 - A.crg[id] * 0.20;                  // 0.13 .. 0.26
  if (vet) brk *= 0.5;
  if (e.team[id] === TEAM.PLAYER) brk *= 0.82;        // the hero's side holds a little longer
  if (act !== ACT.FLEE && (squadRout || m < brk)) {
    A.act[id] = ACT.FLEE; A.actT[id] = 0;
    A.dur[id] = (squadRout ? 3.5 : 4) + frac01(u, 21) * 5;
    A.flags[id] |= AIF.ROUTED;
    dropToken(A, id);
    A.sect[id] = -1;
    e.state[id] = ST.FLEE;
    return;
  }
  if (act === ACT.FLEE) return;

  const tgt = pickTarget(ctx, id);

  if (tgt < 0) {
    A.alert[id] = 0;
    holdPost(ctx, id, sq);
    return;
  }

  const dx = e.x[tgt] - e.x[id], dz = e.z[tgt] - e.z[id];
  const d = hyp(dx, dz);

  // ---- the standard bearer never charges -------------------------------
  // He is the thing his squad forms up on; a banner that runs off into the
  // melee is a squad that has nothing to come back to.
  if ((flags & AIF.BANNER) !== 0 && d > 3.4 && sq >= 0 && S.alive[sq]) {
    A.alert[id] = 0;
    holdPost(ctx, id, sq);
    if (A.act[id] === ACT.IDLE && frac01(u, salt + 3) < 0.22) {
      A.act[id] = ACT.TAUNT; A.actT[id] = 0; A.dur[id] = 1.5 + frac01(u, salt + 5) * 0.5;
      e.state[id] = ST.TAUNT;
      A.yawT[id] = Math.atan2(dx, dz);
    }
    return;
  }

  // ---- awareness ------------------------------------------------------
  const squadEngaged = sq >= 0 && S.order[sq] === ORDERS.ENGAGE;
  if (!A.alert[id]) {
    // a man who has already been in a melee today does not need to be looking
    // the right way to notice the next one
    const engaged = (flags & AIF.ENGAGED) !== 0;
    if (!(squadEngaged && d < 26) && !(engaged && d < R_SENSE) && !notices(ctx, id, tgt)) { holdPost(ctx, id, sq); return; }
    A.alert[id] = 1;
    e.setFlag(id, FLAG.AGGRO, true);
    // reaction beat: nobody charges on the exact frame he notices
    A.think[id] = 0.12 + frac01(u, 33) * 0.35;
  }

  const ranged = A.role[id] === ROLE.ARCHER;

  // ---- run in ---------------------------------------------------------
  if (d > (ranged ? R_ARCHER + 3.5 : R_RING)) {
    if (A.act[id] !== ACT.APPROACH) {
      A.act[id] = ACT.APPROACH; A.actT[id] = 0;
      A.flags[id] |= AIF.ENGAGED;
      // Book a slot on the way in, from the bearing he is arriving on. Every
      // other man closing on the same target has already booked one, so the
      // wave spreads itself around the ring instead of piling onto the face
      // it happened to come from.
      if (!ranged) claimSlot(ctx, id, tgt, Math.atan2(-dx, -dz));
      else A.ringA[id] = Math.atan2(-dx, -dz) + (frac01(u, salt + 7) - 0.5) * 1.6;
    }
    return;
  }

  // ---- in the ring ----------------------------------------------------
  if (A.act[id] === ACT.APPROACH || A.act[id] === ACT.IDLE || A.act[id] === ACT.FORM || A.act[id] === ACT.MARCH) {
    A.act[id] = ACT.RING; A.actT[id] = 0;
    A.flags[id] |= AIF.ENGAGED;
  }
  if (!ranged) claimSlot(ctx, id, tgt, A.sect[id] >= 0 ? A.ringA[id] : Math.atan2(-dx, -dz));

  // Attack? Only with a token, only off cooldown, only in range. The inner
  // rank gets a slightly longer commit range because the windup step carries
  // him the rest of the way in; the second rank is meant to be waiting.
  const commit = ranged ? 16 : (A.rank[id] === 0 ? R_ATTACK + 1.05 : R_ATTACK + 0.35);
  if (A.cool[id] <= 0 && d < commit) {
    if (ranged) {
      if (w.time.sim - arcT > 0.12) {
        arcT = w.time.sim;
        startSwing(ctx, id, tgt);
        return;
      }
    } else if (takeToken(ctx, id, tgt)) {
      startSwing(ctx, id, tgt);
      return;
    }
  }

  // Not swinging: pick something that keeps the ring alive.
  const r = frac01(u, salt + 11);
  const shield = (flags & AIF.SHIELD) !== 0;
  const inner = A.rank[id] === 0;
  if (!ranged && r < (shield ? 0.20 : 0.09) && d < 5) {
    A.act[id] = ACT.BLOCK; A.actT[id] = 0; A.dur[id] = 0.55 + frac01(u, salt + 13) * 0.9;
    e.state[id] = ST.BLOCK;
  } else if (r < 0.26 && d < 6.5 && !inner) {
    // shouting is for the men who are not in the front rank
    A.act[id] = ACT.TAUNT; A.actT[id] = 0; A.dur[id] = 1.45 + frac01(u, salt + 17) * 0.35;
    e.state[id] = ST.TAUNT;
  } else if (!ranged && inner && r < (vet ? 0.56 : 0.44) && d < 5.5 && A.cool[id] < 0.5) {
    // Feint: step inside the ring, threaten, step back out. Costs nothing and
    // it is most of what makes the ring look like it is pressing you — but ONLY
    // from the front rank. A second-rank man who presses drives straight
    // through the first one, which is most of how the "ring" became a scrum.
    A.act[id] = ACT.PRESS; A.actT[id] = 0; A.dur[id] = 0.35 + frac01(u, salt + 19) * 0.4;
    e.state[id] = ST.RUN;
  } else {
    A.act[id] = ACT.RING; e.state[id] = ST.RUN;
    if (r > 0.88) A.ringDir[id] = -A.ringDir[id];
  }
}

/** No enemy in reach: stand in the squad's formation, or on the spawn spot. */
function holdPost(ctx, id, sq) {
  const w = ctx.w, e = w.entities, A = ctx.store;
  const S = ctx.squads;
  A.sect[id] = -1;
  if (sq >= 0 && S.alive[sq]) {
    if (S.order[sq] === ORDERS.ROUT) {
      A.act[id] = ACT.FLEE; A.dur[id] = 4 + frac01(A.spawnKey[id], 21) * 3; A.actT[id] = 0; e.state[id] = ST.FLEE; return;
    }
    const ord = S.order[sq];
    // An engaged squad whose rear rank cannot see the enemy from where it is
    // standing must CLOSE, not dress its line: the squad order says there is a
    // fight 19 m away and a man's own eyes only reach 16.
    if (ord === ORDERS.ENGAGE && S.foeT[sq] < 4.0) {
      const fx = S.foeX[sq] - e.x[id], fz = S.foeZ[sq] - e.z[id];
      if (fx * fx + fz * fz > 6.25) {
        A.homeX[id] = S.foeX[sq]; A.homeZ[id] = S.foeZ[sq];
        A.act[id] = ACT.MARCH; e.state[id] = ST.RUN;
        A.flags[id] |= AIF.ENGAGED;
        return;
      }
    }
    S.slotPos(sq, A.slot[id]);
    A.homeX[id] = _slot.x; A.homeZ[id] = _slot.z;
    const dx = _slot.x - e.x[id], dz = _slot.z - e.z[id];
    // A squad whose anchor is moving must never let its men park: the old code
    // dropped anyone within 1.05 m of his slot into ACT.IDLE (dv = 0), the slot
    // then walked away from him and he ran to catch it, so a marching column
    // advanced as a stutter of stop-start men — which is a large part of why
    // "the column never marches" read as "the column stands still".
    if (ord === ORDERS.ADVANCE || ord === ORDERS.ESCORT) {
      A.act[id] = ACT.MARCH; e.state[id] = ST.RUN;
      return;
    }
    A.act[id] = (dx * dx + dz * dz > 1.1) ? ACT.FORM : ACT.IDLE;
  } else {
    const dx = A.homeX[id] - e.x[id], dz = A.homeZ[id] - e.z[id];
    A.act[id] = (dx * dx + dz * dz > 2.5) ? ACT.FORM : ACT.IDLE;
  }
  if (A.act[id] === ACT.IDLE) {
    // Genuinely idle men get the animator's pantomime (`anim.autoDrive`), which
    // only ever fires from ST.IDLE/ST.WALK — that is why engaged units below
    // never sit in those two states.
    e.state[id] = ST.IDLE;
    A.alert[id] = 0;
  } else e.state[id] = ST.RUN;
}

/* ============================================================ the muscles */

/** Per-frame execution of whatever `think()` decided. */
export function act(ctx, id, dt) {
  const w = ctx.w, e = w.entities, A = ctx.store;
  const a = A.act[id];

  // Transition hook. `block` and `taunt` are held by the animator until it is
  // told to let go (block LOOPS — leaving ST.BLOCK alone would freeze the man
  // in a guard pose for the rest of the battle), so every exit funnels here.
  if (A.prevAct[id] !== a) {
    if (A.prevAct[id] === ACT.BLOCK || A.prevAct[id] === ACT.TAUNT) w.anim?.stop?.(id);
    A.prevAct[id] = a;
  }
  const tgt = A.tgt[id];
  const alive = tgt >= 0 && e.alive[tgt] && e.uid[tgt] === A.tgtUid[id];
  const tx = alive ? e.x[tgt] : 0, tz = alive ? e.z[tgt] : 0;
  const x = e.x[id], z = e.z[id];
  let dx = tx - x, dz = tz - z;
  let d = alive ? hyp(dx, dz) : 1e9;
  const runSpeed = (4.4 + A.agg[id] * 1.4) * A.spdMul[id];

  A.actT[id] += dt;
  if (A.cool[id] > 0) A.cool[id] -= dt;

  switch (a) {
    case ACT.IDLE: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      // slow scan of the horizon so a garrison is not a row of statues
      if (alive && d < 26) A.yawT[id] = Math.atan2(dx, dz);
      else A.yawT[id] = A.yawT[id] + Math.sin(w.time.sim * 0.11 + A.phase[id]) * dt * 0.35;
      break;
    }
    case ACT.FORM: {
      const gx = A.homeX[id], gz = A.homeZ[id];
      const rem = seek(A, id, x, z, gx, gz, 2.0 * A.spdMul[id], 1.6);
      A.yawT[id] = rem > 0.6 ? Math.atan2(gx - x, gz - z) : facingOf(ctx, id);
      if (rem < 0.45) { A.act[id] = ACT.IDLE; e.state[id] = ST.RUN; }
      break;
    }
    case ACT.MARCH: {
      const gx = A.homeX[id], gz = A.homeZ[id];
      // A squad ordered forward moves its formation ANCHOR at `squads.speed`
      // (2-4.5 m/s) while its men walked to their slots at a flat 2.0, so a
      // marching column fell permanently behind its own slots and a wave of
      // reinforcements could never catch a hero who was running. Take the march
      // pace from the squad and stay slightly ahead of the anchor.
      const sqm = A.squad[id];
      const S2 = ctx.squads;
      const march = (sqm >= 0 && S2.alive[sqm]
        ? (S2.order[sqm] === ORDERS.ENGAGE ? 4.4 : Math.max(2.0, S2.speed[sqm] * 1.3)) : 2.0);
      const rem = seek(A, id, x, z, gx, gz, march * A.spdMul[id], 1.15);
      A.yawT[id] = rem > 0.5 ? Math.atan2(gx - x, gz - z) : facingOf(ctx, id);
      e.state[id] = ST.RUN;
      break;
    }
    case ACT.APPROACH: {
      if (!alive) { A.act[id] = ACT.IDLE; break; }
      // Aim at a point on the ring, not at the man himself: twenty units all
      // seeking the same centre arrive as a wall and then interpenetrate.
      const rr = standR(A, id) + 0.2;
      const gx = tx + Math.sin(A.ringA[id]) * rr, gz = tz + Math.cos(A.ringA[id]) * rr;
      // per-unit weave so a charge is not a set of parallel straight lines
      const wob = Math.sin(w.time.sim * 1.7 + A.phase[id]) * 0.55 * Math.min(1, d / 12);
      seek(A, id, x, z, gx + Math.cos(A.ringA[id]) * wob, gz - Math.sin(A.ringA[id]) * wob, runSpeed, 2.2);
      A.yawT[id] = Math.atan2(dx, dz);
      e.state[id] = ST.RUN;
      break;
    }
    case ACT.RING: {
      if (!alive) { A.act[id] = ACT.IDLE; break; }
      const ranged = A.role[id] === ROLE.ARCHER;
      A.ringA[id] += A.ringDir[id] * A.orbit[id] * dt;
      // in/out breathing keeps every man in the ring moving a little, always.
      // Depth is the SECOND RANK (standR), not a global push outward: a mob
      // that widens as it grows walks itself out of its own attack range.
      const rr = (ranged ? R_ARCHER : standR(A, id)) + Math.sin(w.time.sim * 0.8 + A.phase[id]) * 0.28;
      const gx = tx + Math.sin(A.ringA[id]) * rr, gz = tz + Math.cos(A.ringA[id]) * rr;
      seek(A, id, x, z, gx, gz, 2.6 * A.spdMul[id], 1.5);
      A.yawT[id] = Math.atan2(dx, dz);
      e.state[id] = ST.RUN;
      // Ready, in the front rank, in range: do not sit on a stale think timer
      // for another third of a second — that latency alone cost the ring most
      // of its swings.
      if (!ranged && A.rank[id] === 0 && A.cool[id] <= 0 && d < 3.5 && A.think[id] > 0.09
        && A.tokN[tgt] < (tgt === w.playerId ? ctx.tokens + ctx.open * 2 : ctx.tokensAI)) {
        A.think[id] = 0.05 + frac01(A.spawnKey[id], 77) * 0.05;
      }
      break;
    }
    case ACT.PRESS: {
      if (!alive) { A.act[id] = ACT.IDLE; break; }
      const t = A.actT[id] / A.dur[id];
      const rr = t < 0.55 ? 1.95 : standR(A, id);
      const gx = tx + Math.sin(A.ringA[id]) * rr, gz = tz + Math.cos(A.ringA[id]) * rr;
      seek(A, id, x, z, gx, gz, 3.2 * A.spdMul[id], 0.9);
      A.yawT[id] = Math.atan2(dx, dz);
      if (A.actT[id] >= A.dur[id]) { A.act[id] = ACT.RING; }
      break;
    }
    case ACT.BLOCK: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      if (alive) A.yawT[id] = Math.atan2(dx, dz);
      if (A.actT[id] >= A.dur[id]) {
        A.act[id] = ACT.RING; e.state[id] = ST.RUN;
        // `block` is a LOOPING clip: leaving ST.BLOCK is not enough to end it,
        // the animator needs to be told to fall back to locomotion.
        w.anim?.stop?.(id);
      }
      break;
    }
    case ACT.TAUNT: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      if (alive) A.yawT[id] = Math.atan2(dx, dz);
      if (A.actT[id] >= A.dur[id]) { A.act[id] = ACT.RING; e.state[id] = ST.RUN; w.anim?.stop?.(id); }
      break;
    }
    case ACT.WINDUP: {
      const t = A.actT[id] / A.dur[id];
      if (alive) A.yawT[id] = Math.atan2(dx, dz);
      // lean into the blow — a swing that does not travel has no weight
      const step = A.role[id] === ROLE.ARCHER ? 0 : (d > 2.2 ? 3.0 : 0.6);
      if (alive && step > 0) seek(A, id, x, z, tx, tz, step, 1.0); else { A.dvx[id] = 0; A.dvz[id] = 0; }
      if (t >= 0.42) {
        A.act[id] = ACT.SWING; e.state[id] = ST.ATTACK;
        land(ctx, id, tgt);
      }
      break;
    }
    case ACT.SWING: {
      A.dvx[id] *= 0.5; A.dvz[id] *= 0.5;
      if (A.actT[id] >= A.dur[id] * 0.60) { A.act[id] = ACT.RECOVER; e.state[id] = ST.RECOVER; }
      break;
    }
    case ACT.RECOVER: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      if (A.actT[id] >= A.dur[id]) {
        dropToken(A, id);
        A.act[id] = ACT.RING; e.state[id] = ST.RUN;
        const vet = (A.flags[id] & AIF.VETERAN) !== 0;
        A.cool[id] = (ctx.coolMin + frac01(A.spawnKey[id], w.time.tick & 255) * ctx.coolSpan) * (vet ? 0.62 : 1)
          + (A.role[id] === ROLE.ARCHER ? 1.8 : 0);
        A.think[id] = Math.min(A.think[id], 0.12);
        // A man who has just swung falls back through the ring and lets the
        // next rank in. Without this the front rank is the same eight men for
        // the whole fight and the ones behind them never get a turn.
        if (A.rank[id] === 0 && !vet && frac01(A.spawnKey[id], (w.time.tick & 255) + 91) < 0.42) {
          const t = A.tgt[id], s = A.sect[id];
          if (t >= 0 && s >= 0 && A.ringSlot[(t << 5) + s] > 0) A.ringSlot[(t << 5) + s]--;
          A.sect[id] = -1; A.rank[id] = 1;
        }
      }
      break;
    }
    case ACT.FLINCH: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      if (A.actT[id] >= A.dur[id]) {
        A.act[id] = alive ? ACT.RING : ACT.IDLE;
        e.state[id] = alive ? ST.RUN : ST.IDLE;
        e.stagger[id] = 0;
        A.think[id] = 0.05;
      }
      break;
    }
    case ACT.LAUNCHED: {
      if (ballistic(w, A, id, dt)) {
        A.act[id] = ACT.DOWN; A.actT[id] = 0;
        A.dur[id] = 0.55 + frac01(A.spawnKey[id], 41) * 0.9;
        e.state[id] = ST.DOWN;
        _ao.blend = 0.06; w.anim?.play?.(id, 'knockdown', _ao);
        w.vfx?.dust?.(e.x[id], e.y[id], e.z[id], 0.6);
      } else if (e.state[id] === ST.LAUNCH && A.actT[id] > 0.3) e.state[id] = ST.AIRBORNE;
      return true;                                  // handled its own physics
    }
    case ACT.DOWN: {
      e.vx[id] *= Math.exp(-8 * dt); e.vz[id] *= Math.exp(-8 * dt);
      A.dvx[id] = 0; A.dvz[id] = 0;
      if (A.actT[id] >= A.dur[id]) {
        A.act[id] = ACT.GETUP; A.actT[id] = 0; A.dur[id] = 0.95;
        e.state[id] = ST.GETUP;
        _ao.blend = 0.1; w.anim?.play?.(id, 'getup', _ao);
      }
      break;
    }
    case ACT.GETUP: {
      A.dvx[id] = 0; A.dvz[id] = 0;
      if (alive) A.yawT[id] = Math.atan2(dx, dz);
      if (A.actT[id] >= A.dur[id]) {
        A.act[id] = ACT.RING; e.state[id] = ST.RUN;
        A.cool[id] = 0.25 + frac01(A.spawnKey[id], 47) * 0.6;
        e.morale[id] = Math.max(0, e.morale[id] - 0.06);
      }
      break;
    }
    case ACT.FLEE: {
      // A rout is a UNIT streaming to the rear, not a dozen men scattering on
      // a dozen bearings: men fleeing radially left 22 isolated bodies on a map
      // that had none. If his squad is running, he runs where it is running.
      const sqf = A.squad[id], Sf = ctx.squads;
      const speed = (5.2 + A.agg[id]) * A.spdMul[id];
      if (sqf >= 0 && Sf.alive[sqf] && Sf.order[sqf] === ORDERS.ROUT) {
        seek(A, id, x, z, Sf.gx[sqf], Sf.gz[sqf], speed, 3.0);
      } else {
        const px = alive ? tx : e.x[id] + Math.sin(e.yaw[id] + 3.14) * 10;
        const pz = alive ? tz : e.z[id] + Math.cos(e.yaw[id] + 3.14) * 10;
        flee(A, id, x, z, px, pz, speed);
        // and bend the run back towards his own formation so he does not solo
        // off across the valley
        if (sqf >= 0 && Sf.alive[sqf]) {
          const hx = Sf.ax[sqf] - x, hz = Sf.az[sqf] - z;
          const hl = Math.sqrt(hx * hx + hz * hz);
          if (hl > 9) { A.dvx[id] += (hx / hl) * speed * 0.75; A.dvz[id] += (hz / hl) * speed * 0.75; }
        }
      }
      A.yawT[id] = Math.atan2(A.dvx[id], A.dvz[id]);
      e.state[id] = ST.FLEE;
      if (A.actT[id] >= A.dur[id]) {
        // rallied — a routed man who survives long enough comes back angry
        e.morale[id] = 0.42 + A.crg[id] * 0.3;
        A.act[id] = ACT.IDLE; A.alert[id] = 0; A.tgt[id] = -1; e.state[id] = ST.IDLE;
      }
      break;
    }
    default: { A.dvx[id] = 0; A.dvz[id] = 0; break; }
  }
  return false;
}

function facingOf(ctx, id) {
  const A = ctx.store, sq = A.squad[id];
  if (sq >= 0 && ctx.squads.alive[sq]) return Math.atan2(ctx.squads.fx[sq], ctx.squads.fz[sq]);
  return ctx.w.entities.yaw[id];
}

/* ============================================================== reactions */

/**
 * A man standing next to a man who just died.
 *
 * The only thing that used to happen here was a -0.055 morale scalar, and the
 * scalar's single consumer was one binary threshold that never fired. Nobody
 * flinched, nobody looked, nobody gave ground — twenty-five men could drop
 * inside one sweep and the survivors kept circling on the same beat. This is
 * the visible half of that: a short guard-up recoil, a look at the body, and a
 * step away from it. Called at most a handful of times per death.
 */
export function witness(ctx, id, dx, dz, d, close) {
  const w = ctx.w, e = w.entities, A = ctx.store;
  const a = A.act[id];
  if (LOCKED[a] || a === ACT.FLEE || a === ACT.DUEL) return;
  const u = A.spawnKey[id];
  // look at it
  A.yawT[id] = Math.atan2(dx, dz);
  if (!close) return;
  const r = frac01(u, (w.time.tick & 255) + 61);
  const vet = (A.flags[id] & AIF.VETERAN) !== 0;
  if (r > (vet ? 0.35 : 0.72)) return;              // veterans mostly do not flinch
  dropToken(A, id);
  A.act[id] = ACT.BLOCK; A.actT[id] = 0;
  A.dur[id] = 0.30 + frac01(u, 63) * 0.28;
  A.think[id] = A.dur[id] + 0.04;
  e.state[id] = ST.BLOCK;      // the animator's state map owns the clip
  // and give ground: a shove away from the body, which reads as a stumble back
  const k = (1 - Math.min(1, d / 4.5)) * (vet ? 0.9 : 2.1);
  e.vx[id] -= dx * k; e.vz[id] -= dz * k;
}

/**
 * Take a hit. Called from the bus (`'hit'`, i.e. the player's combat system)
 * and from `ai.hurt` for unit-vs-unit blows.
 */
export function react(ctx, id, amount, srcId, dirX, dirZ, heavy) {
  const w = ctx.w, e = w.entities, A = ctx.store;
  A.ensure(e, id);
  dropToken(A, id);
  A.alert[id] = 1;
  A.flags[id] |= AIF.ENGAGED;
  e.setFlag(id, FLAG.AGGRO, true);

  // whoever hit me is now my problem
  if (srcId >= 0 && srcId !== id && e.alive[srcId] && e.team[srcId] !== e.team[id]) {
    if (A.tgt[id] !== srcId) { A.tgt[id] = srcId; A.tgtUid[id] = e.uid[srcId]; A.sect[id] = -1; A.rank[id] = 0; }
    const bx = e.x[id] - e.x[srcId], bz = e.z[id] - e.z[srcId];
    A.ringA[id] = Math.atan2(bx, bz);
  }
  e.morale[id] = Math.max(0, e.morale[id] - (heavy ? 0.10 : 0.045));

  const launch = heavy > 1;
  if (launch) {
    A.act[id] = ACT.LAUNCHED; A.actT[id] = 0;
    e.state[id] = ST.LAUNCH; e.stateT[id] = 0;
    e.vy[id] = 5.6 + Math.min(3.5, amount * 0.05);
    e.vx[id] = dirX * 5.5; e.vz[id] = dirZ * 5.5;
    e.stagger[id] = 1;
    e.setFlag(id, FLAG.JUGGLED, true);
    // combat owns the counter but not this code path (combat.js react() is
    // the no-AI fallback), so tell it, or stats.launched reads 0 all battle.
    w.combat?.noteLaunch?.();
    _ao.blend = 0.05; w.anim?.play?.(id, 'launch', _ao);
  } else {
    A.act[id] = ACT.FLINCH; A.actT[id] = 0;
    A.dur[id] = (heavy ? 0.52 : 0.30) * ((A.flags[id] & AIF.VETERAN) ? 0.72 : 1);
    e.state[id] = ST.HIT; e.stateT[id] = 0;
    e.stagger[id] = heavy ? 0.7 : 0.2;
    const kb = heavy ? 4.6 : 1.8;
    e.vx[id] += dirX * kb; e.vz[id] += dirZ * kb;
    _ao.blend = 0.05; w.anim?.play?.(id, heavy ? 'hit_heavy' : 'hit_light', _ao);
  }
  A.think[id] = A.dur[id] + 0.05;
}

export { R_ATTACK, R_HIT, R_RING, R_SENSE, LEASH, RANK_GAP, ATTACKS };
export { claimSlot };
